import { useApi } from '../../../../../core/api/api.context';
import { useMutation, useQueryClient } from 'react-query';
import {
  BookOrderDto,
  CreateCustomerDto,
  CustomerDto,
  SearchCustomerDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { QueryKeys } from '../../../../../core/queries/query-keys.constants';
import { fetchAllPages } from '../../../../../core/queries/query-helpers';

export const useMutationCustomerSearch = () => {
  const api = useApi();

  return useMutation(async ({ projectID, ...data }: SearchCustomerDto & { projectID: string }) => {
    if (projectID === undefined) {
      return undefined;
    }
    return await fetchAllPages(
      async (pageNo) =>
        await api.customersController.customersSearch(projectID, data, {
          pageNo
        })
    );
  });
};

export const useMutationBookOrder = () => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation(
    async ({
      projectID,
      customerData,
      orderData,
      customerID
    }: {
      projectID: string;
      customerData?: CreateCustomerDto;
      orderData: Omit<BookOrderDto, 'customerID'>;
      customerID?: string;
    }) => {
      let finalCustomerID = customerID;
      let createdCustomer: CustomerDto | undefined;

      // Create customer if customer data is provided
      if (customerData) {
        createdCustomer = await api.customersController.customersCreate(projectID, customerData);
        finalCustomerID = createdCustomer.id;
      }

      if (!finalCustomerID) {
        throw new Error('Either customerID or customerData must be provided');
      }

      // Prepare order payload
      const payload = { ...orderData };
      if (payload.hubID === 'any') payload.hubID = undefined;

      // Create order
      const order = await api.ordersController.ordersCreate(projectID, {
        ...payload,
        customerID: finalCustomerID
      });

      // Invalidate queries
      if (customerData) {
        await queryClient.invalidateQueries(QueryKeys.CUSTOMERS);
      }
      await queryClient.invalidateQueries(QueryKeys.ORDERS);

      return { order, createdCustomer };
    }
  );
};

export const useMutationBookOrderWithImmediateDelivery = () => {
  const queryClient = useQueryClient();
  const api = useApi();

  return useMutation(
    async ({
      projectID,
      customerData,
      orderData,
      customerID,
      lockerID
    }: {
      projectID: string;
      customerData?: CreateCustomerDto;
      orderData: Omit<BookOrderDto, 'customerID'>;
      customerID?: string;
      lockerID: string;
      openDoor?: boolean;
    }) => {
      let finalCustomerID = customerID;
      let createdCustomer: CustomerDto | undefined;

      // Create customer if customer data is provided
      if (customerData) {
        createdCustomer = await api.customersController.customersCreate(projectID, customerData);
        finalCustomerID = createdCustomer.id;
      }

      if (!finalCustomerID) {
        throw new Error('Either customerID or customerData must be provided');
      }

      // Prepare order payload
      const payload = { ...orderData };
      if (payload.hubID === 'any') payload.hubID = undefined;

      // Create order
      const order = await api.ordersController.ordersCreate(projectID, {
        ...payload,
        customerID: finalCustomerID
      });

      // Create immediate delivery with specific locker assignment using deliverViaCloud
      const delivery = await api.ordersActionsController.deliverViaCloud(projectID, order.id, {
        hubID: payload.hubID || orderData.hubID!,
        deliveryContainerID: lockerID,
        openDoor: false
      });

      // Invalidate queries
      if (customerData) {
        await queryClient.invalidateQueries(QueryKeys.CUSTOMERS);
      }
      await queryClient.invalidateQueries(QueryKeys.ORDERS);
      await queryClient.invalidateQueries(QueryKeys.DELIVERIES);

      return { order, delivery, createdCustomer };
    }
  );
};
